export interface AdminUserRow {
  id: string;
  email: string;
  name: string | null;
  clerkId: string;
  createdAt: string;
  disabledAt: string | null;
  lastSeenAt: string | null;
  taskCount: number;
  lastActivityAt: string | null;
}

export interface AdminUsersPage {
  page: number;
  pageSize: number;
  total: number;
  users: AdminUserRow[];
}

export interface AdminActivityRow {
  id: string;
  userId: string;
  email: string | null;
  kind: string;
  payload: unknown;
  createdAt: string;
}

export interface AdminActivityKindCount {
  kind: string;
  eventCount: number;
}

export interface AdminActivity {
  days: number;
  since: string;
  limit: number;
  userId: string | null;
  rows: AdminActivityRow[];
  byKind: AdminActivityKindCount[];
}

export interface AdminAiUsageRow {
  userId: string;
  email: string | null;
  callCount: number;
  dailyCap: number;
  usedToday: number;
  saturation: number;
  overEightyPct: boolean;
}

export interface AdminAiUsage {
  days: number;
  since: string;
  rows: AdminAiUsageRow[];
}

export interface AdminMailFailureGroup {
  userId: string;
  email: string | null;
  failureCount: number;
  lastFailureAt: string;
}

export interface AdminMailFailureRow {
  id: string;
  userId: string;
  email: string | null;
  messageId: string;
  accountId: string | null;
  error: string;
  createdAt: string;
}

export interface AdminMailFailures {
  days: number;
  since: string;
  grouped: AdminMailFailureGroup[];
  recent: AdminMailFailureRow[];
}

export interface AdminOverview {
  users: {
    total: number;
    disabled: number;
    active7Days: number;
    active30Days: number;
  };
  tasks: { total: number };
  projects: { total: number };
  mails: { total: number; triaged: number };
  aiCalls: { total: number; last7Days: number; last30Days: number };
}

import { getImpersonationHeaders } from "./impersonation";

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...getImpersonationHeaders(),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (typeof body?.error === "string") msg = body.error;
    } catch {
      /* ignore */
    }
    const e = new Error(msg) as Error & { status?: number };
    e.status = r.status;
    throw e;
  }
  return (await r.json()) as T;
}

export interface AdminUserDetail {
  id: string;
  email: string;
  name: string | null;
  clerkId: string;
  createdAt: string;
  disabledAt: string | null;
  lastSeenAt: string | null;
  plan: "free" | "pro" | "unlimited" | string;
  aiCreditsRemaining: number;
  planRenewsAt: string | null;
  planGifted: boolean;
  dailyCap: number | null;
  rawDailyCap: number;
  mailAccountCount: number;
  usedToday: number;
  lastActivityAt: string | null;
  recentBillingEvents: AdminBillingEventRow[];
}

export interface AdminBillingEventRow {
  id: string;
  userId: string;
  userEmail?: string | null;
  actorUserId: string | null;
  actorEmail?: string | null;
  actorIsAdmin?: boolean;
  type: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface AdminBillingEventsPage {
  page: number;
  pageSize: number;
  total: number;
  rows: AdminBillingEventRow[];
}

export interface AdminAiCostMonth {
  yearMonth: string;
  totalEur: number;
  promptTokens: number;
  completionTokens: number;
  callCount: number;
  budgetEur: number;
  killSwitchArmed: boolean;
  manuallyTrippedAt: string | null;
  manuallyTrippedBy: string | null;
  alertedAt80: string | null;
  percentOfBudget: number;
}

export interface AdminAiCostFeature {
  feature: string;
  totalEur: number;
  callCount: number;
  promptTokens: number;
  completionTokens: number;
}

export interface AdminAiCosts {
  current: AdminAiCostMonth;
  history: AdminAiCostMonth[];
  breakdown: AdminAiCostFeature[];
}

export interface AdminReferralCode {
  id: string;
  code: string;
  label: string;
  notes: string | null;
  active: boolean;
  createdBy: string;
  createdAt: string;
  signupCount: number;
  lastSignupAt: string | null;
}

export interface AdminReferralUser {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  plan: string;
  disabledAt: string | null;
}

export const adminApi = {
  listUsers: (params: { page?: number; pageSize?: number; search?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.pageSize) qs.set("pageSize", String(params.pageSize));
    if (params.search) qs.set("search", params.search);
    return jsonFetch<AdminUsersPage>(`/api/admin/users?${qs.toString()}`);
  },
  aiUsage: (days = 7) =>
    jsonFetch<AdminAiUsage>(`/api/admin/usage/ai?days=${days}`),
  mailFailures: (days = 7) =>
    jsonFetch<AdminMailFailures>(`/api/admin/mail/failures?days=${days}`),
  overview: () => jsonFetch<AdminOverview>(`/api/admin/metrics/overview`),
  disableUser: (id: string) =>
    jsonFetch<{ ok: boolean }>(`/api/admin/users/${id}/disable`, {
      method: "POST",
    }),
  enableUser: (id: string) =>
    jsonFetch<{ ok: boolean }>(`/api/admin/users/${id}/enable`, {
      method: "POST",
    }),
  resetAiQuota: (id: string) =>
    jsonFetch<{
      ok: boolean;
      deletedRows: number;
      mailAiUsageReset: number;
    }>(`/api/admin/users/${id}/reset-ai-quota`, { method: "POST" }),
  getUser: (id: string) =>
    jsonFetch<AdminUserDetail>(`/api/admin/users/${id}`),
  patchUser: (
    id: string,
    body: {
      plan?: "free" | "pro" | "unlimited";
      aiCreditsRemaining?: number;
      dailyAiAnalysisCap?: number;
      planGifted?: boolean;
    },
  ) =>
    jsonFetch<AdminUserDetail>(`/api/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  grantUser: (
    id: string,
    body: { kind: "credits" | "discount"; amount: number; reason: string },
  ) =>
    jsonFetch<AdminUserDetail>(`/api/admin/users/${id}/grant`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  billingEvents: (
    params: {
      page?: number;
      pageSize?: number;
      userId?: string;
      type?: string;
      days?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.pageSize) qs.set("pageSize", String(params.pageSize));
    if (params.userId) qs.set("userId", params.userId);
    if (params.type) qs.set("type", params.type);
    if (params.days) qs.set("days", String(params.days));
    return jsonFetch<AdminBillingEventsPage>(
      `/api/admin/billing-events?${qs.toString()}`,
    );
  },
  aiCosts: () => jsonFetch<AdminAiCosts>(`/api/admin/ai-costs`),
  aiCostsReset: () =>
    jsonFetch<{ ok: boolean; summary: AdminAiCostMonth }>(
      `/api/admin/ai-costs/reset`,
      { method: "POST" },
    ),
  aiCostsTrip: () =>
    jsonFetch<{ ok: boolean; summary: AdminAiCostMonth }>(
      `/api/admin/ai-costs/trip`,
      { method: "POST" },
    ),
  aiCostsRelease: () =>
    jsonFetch<{ ok: boolean; summary: AdminAiCostMonth }>(
      `/api/admin/ai-costs/release`,
      { method: "POST" },
    ),
  activity: (params: { days?: number; userId?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.days) qs.set("days", String(params.days));
    if (params.userId) qs.set("userId", params.userId);
    if (params.limit) qs.set("limit", String(params.limit));
    return jsonFetch<AdminActivity>(`/api/admin/activity?${qs.toString()}`);
  },

  // ── Referral codes ──────────────────────────────────────────────────────
  listReferrals: () =>
    jsonFetch<AdminReferralCode[]>(`/api/admin/referrals`),

  createReferral: (body: { code: string; label: string; notes?: string }) =>
    jsonFetch<AdminReferralCode>(`/api/admin/referrals`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  patchReferral: (
    id: string,
    body: { label?: string; notes?: string; active?: boolean },
  ) =>
    jsonFetch<AdminReferralCode>(`/api/admin/referrals/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteReferral: (id: string) =>
    jsonFetch<{ ok: boolean }>(`/api/admin/referrals/${id}`, {
      method: "DELETE",
    }),

  listReferralUsers: (id: string) =>
    jsonFetch<AdminReferralUser[]>(`/api/admin/referrals/${id}/users`),
};
